import type { ReactNode } from 'react'
import { errorMessage, isApiError, isSessionEnded } from '@/api/client'
import { cn } from '@/lib/format'
import { Button } from './Button'
import { AlertIcon, InfoIcon, SpinnerIcon } from './icons'

/** Centered spinner with a visible label. */
export function LoadingState({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={cn('flex items-center justify-center gap-2 py-12 text-sm text-muted', className)}>
      <SpinnerIcon size={16} />
      <span>{label}</span>
    </div>
  )
}

/** Placeholder bars while a list or card loads. Announces "Loading". */
export function SkeletonRows({ rows = 5, className, label = 'Loading' }: { rows?: number; className?: string; label?: string }) {
  return (
    <div role="status" aria-label={label} className={cn('divide-y divide-line', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3.5">
          <div className="h-3 w-1/3 animate-pulse rounded bg-hover" />
          <div className="h-3 w-1/5 animate-pulse rounded bg-hover/70" />
          <div className="ml-auto h-3 w-16 animate-pulse rounded bg-hover/70" />
        </div>
      ))}
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  description?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      <p className="font-display text-xl font-bold uppercase tracking-wide text-ink">{title}</p>
      {description ? <p className="max-w-md text-sm text-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

export interface ErrorStateProps {
  error: unknown
  title?: string
  onRetry?: () => void
  className?: string
}

/** A failed load: what went wrong, plus Retry. */
export function ErrorState({ error, title, onRetry, className }: ErrorStateProps) {
  // The session ended: the route guard is on its way to /login?next=….
  if (isSessionEnded() && isApiError(error) && error.status === 401) return null
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      <AlertIcon size={22} className="text-bad" />
      <p className="font-semibold text-ink">{title ?? "Couldn't load this"}</p>
      <p className="max-w-md text-sm text-muted">{errorMessage(error)}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry} className="mt-2">
          Retry
        </Button>
      ) : null}
    </div>
  )
}

export interface InlineAlertProps {
  tone?: 'info' | 'warn' | 'bad' | 'good'
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
  className?: string
}

const ALERT_TONES = {
  info: 'border-line-strong bg-raised text-ink',
  warn: 'border-warn/30 bg-warn-soft text-warn',
  bad: 'border-bad/30 bg-bad-soft text-bad',
  good: 'border-good/30 bg-good-soft text-good',
}

/** A banner inside a page or card. `bad` is announced as an alert. */
export function InlineAlert({ tone = 'info', title, children, action, className }: InlineAlertProps) {
  return (
    <div role={tone === 'bad' ? 'alert' : undefined} className={cn('flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-[13px]', ALERT_TONES[tone], className)}>
      {tone === 'info' ? <InfoIcon size={16} className="mt-0.5 shrink-0 text-muted" /> : <AlertIcon size={16} className="mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title ? 'mt-0.5' : null, tone !== 'info' && 'text-ink/90')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
